import { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import {
  TabKicker,
  TabSectionTitle,
  HeroCard,
  ChartCard,
} from "./_shell";
import GradesComparator from "./GradesComparator";

/**
 * TabAdmissionExtras — editorial split-hero.
 *   • Hero card (col-span 4): acceptance rate + source
 *   • Chart card (col-span 8): UCAS tariff histogram with peer average line
 *
 * All data-testids from the previous implementation are preserved.
 */

// HESA tariff-band code (T144, T160, ...) → the UCAS tariff-point range
// that band covers. HESA publishes intake in 16-point bins starting at
// T048 (48pts). We keep the first bin (T001) as "<48" since it also
// captures any candidate admitted below the C-grade floor.
const BAND_RANGE = {
  T001: "<48",  T048: "48–63",  T064: "64–79",  T080: "80–95",
  T096: "96–111",  T112: "112–127",  T128: "128–143",  T144: "144–159",
  T160: "160–175",  T176: "176–191",  T192: "192–207",  T208: "208–223",
  T224: "224–239",  T240: "240+",
};
// Anchor grades so a non-UK reader can decode the shape at a glance.
const BAND_ANCHOR = {
  T128: "BBB",  T144: "AAB",  T160: "A*AA",  T176: "A*A*A",  T240: "A*A*A + extras",
};

function TariffHistogram({ dist, userTariff }) {
  if (!dist?.buckets?.length) return null;
  const total = dist.total_students || 1;
  const counts = dist.buckets.map((b) => b.count);
  const maxCount = Math.max(...counts);
  const avg = counts.reduce((a, b) => a + b, 0) / counts.length;
  const modalIdx = counts.indexOf(maxCount);
  const W = 640, H = 240, PAD_X = 32, PAD_TOP = 24, PAD_BOTTOM = 52;
  const barW = (W - 2 * PAD_X) / dist.buckets.length - 6;
  const plotH = H - PAD_TOP - PAD_BOTTOM;
  const yFor = (v) => PAD_TOP + plotH - (maxCount ? (v / maxCount) * plotH : 0);
  const avgY = yFor(avg);

  // Locate the bar index a user's UCAS tariff falls in so we can drop
  // a marker on the correct column. Bands use `T` prefix with the
  // lower bound in points (T144 = 144-159).
  let userBarIdx = -1;
  if (userTariff != null) {
    for (let i = 0; i < dist.buckets.length; i++) {
      const lo = parseInt(dist.buckets[i].band.replace("T", ""), 10);
      const nextLo = i + 1 < dist.buckets.length
        ? parseInt(dist.buckets[i + 1].band.replace("T", ""), 10)
        : Infinity;
      if (userTariff >= lo && userTariff < nextLo) { userBarIdx = i; break; }
    }
  }

  return (
    <div data-testid="tariff-histogram">
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} className="max-w-full h-auto" role="img"
           aria-label="UCAS tariff distribution of enrolled students">
        <line x1={PAD_X} y1={PAD_TOP + plotH} x2={W - PAD_X} y2={PAD_TOP + plotH}
              stroke="#002147" strokeOpacity="0.2" strokeWidth="1" />
        {dist.buckets.map((b, i) => {
          const h = maxCount ? (b.count / maxCount) * plotH : 0;
          const x = PAD_X + i * (barW + 6);
          const y = PAD_TOP + plotH - h;
          const pctNum = (b.count / total) * 100;
          const pct = pctNum >= 10 ? pctNum.toFixed(0) : pctNum.toFixed(1);
          const isModal = i === modalIdx;
          const isUser = i === userBarIdx;
          return (
            <g key={b.band}>
              <rect x={x} y={y} width={barW} height={h}
                    fill={isUser ? "#B45309" : "#7C3AED"}
                    fillOpacity={isUser ? 0.95 : (isModal ? 0.95 : 0.5)}>
                <title>{`${b.band} (${BAND_RANGE[b.band] || b.band} pts) · ${b.count.toLocaleString()} students · ${pct}%`}</title>
              </rect>
              {isModal && !isUser && (
                <text x={x + barW / 2} y={y - 6} textAnchor="middle"
                      fill="#7C3AED"
                      style={{ fontSize: 12, fontFamily: "ui-monospace, monospace", fontWeight: 700 }}>
                  {pct + "% MODAL"}
                </text>
              )}
              {isUser && (
                <>
                  <text x={x + barW / 2} y={y - 18} textAnchor="middle"
                        fill="#B45309"
                        style={{ fontSize: 12, fontFamily: "ui-monospace, monospace", fontWeight: 700 }}>
                    YOU
                  </text>
                  <text x={x + barW / 2} y={y - 6} textAnchor="middle"
                        fill="#B45309"
                        style={{ fontSize: 10, fontFamily: "ui-monospace, monospace", fontWeight: 500 }}>
                    {userTariff + " pts"}
                  </text>
                </>
              )}
              <text x={x + barW / 2} y={PAD_TOP + plotH + 14} textAnchor="middle"
                    fill="#002147" fillOpacity={0.7}
                    style={{ fontSize: 9, fontFamily: "ui-monospace, monospace" }}>
                {BAND_RANGE[b.band] || b.band}
              </text>
              {BAND_ANCHOR[b.band] && (
                <text x={x + barW / 2} y={PAD_TOP + plotH + 26} textAnchor="middle"
                      fill="#7C3AED"
                      style={{ fontSize: 9, fontFamily: "ui-monospace, monospace", fontWeight: 600, letterSpacing: 0.5 }}>
                  {BAND_ANCHOR[b.band]}
                </text>
              )}
            </g>
          );
        })}
        <line x1={PAD_X} y1={avgY} x2={W - PAD_X} y2={avgY}
              stroke="#002147" strokeWidth="1.5" strokeDasharray="4 3" />
        <text x={W - PAD_X - 4} y={avgY - 4} textAnchor="end"
              fill="#002147"
              style={{ fontSize: 9, fontFamily: "ui-monospace, monospace" }}>
          UNI AVG · {avg.toFixed(0)}
        </text>
        <text x={W / 2} y={H - 6} textAnchor="middle"
              fill="#002147" fillOpacity={0.6}
              style={{ fontSize: 10, fontFamily: "ui-monospace, monospace", letterSpacing: 1.2 }}>
          UCAS TARIFF POINTS · % OF ENROLLED
        </text>
      </svg>
      <p className="mt-3 font-mono text-[10px] uppercase tracking-wider text-[#002147]/50">
        {dist.n_courses} courses · {dist.total_students.toLocaleString()} students · HESA KIS 2023-24 · <span className="normal-case tracking-normal">A-Level equivalents: A*=56, A=48, B=40, C=32 pts</span>
      </p>
    </div>
  );
}

/**
 * Shared fetch hook — used by all three exports below so we only hit
 * `/admission-signals` once per uni on this page.
 */
function useAdmissionSignals(uniId) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get(`/universities/${uniId}/admission-signals`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [uniId]);
  return data;
}

export function AcceptanceRateHero({ uni }) {
  const data = useAdmissionSignals(uni.id);
  if (!data || data.acceptance_rate == null) return null;
  const evidenceUrl = data.offer_rate_evidence?.source_url;
  return (
    <HeroCard
      kicker="Acceptance rate"
      metric={`${Math.round(data.acceptance_rate * 100)}%`}
      subtitle="last cycle"
      prose={
        <>
          Share of applicants who received an offer. Includes contextual
          offers where the university files them centrally.
          {evidenceUrl && (
            <a href={evidenceUrl} target="_blank" rel="noopener noreferrer"
               className="mt-3 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-[#7C3AED] hover:underline">
              Source · {new URL(evidenceUrl).hostname} <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </>
      }
      data-testid="admission-acceptance-rate-card"
    >
      <span className="sr-only" data-testid="admission-acceptance-rate">
        {Math.round(data.acceptance_rate * 100)}%
      </span>
    </HeroCard>
  );
}

export function TariffHistogramCard({ uni }) {
  const data = useAdmissionSignals(uni.id);
  const [bandsCount, setBandsCount] = useState(0);
  const [userTariff, setUserTariff] = useState(null);
  useEffect(() => {
    if (!uni?.id) return;
    api.get(`/universities/${uni.id}/admit-bands`).then((r) => {
      setBandsCount(Object.keys(r.data?.bands || {}).length);
    }).catch(() => setBandsCount(0));
  }, [uni?.id]);
  const hasHist = Boolean(data?.tariff_distribution);
  if (!data) return null;
  // Show the section whenever we have either the UK UCAS histogram
  // OR at least one editorial admit band on file — otherwise the
  // comparator has nothing to verdict against.
  if (!hasHist && bandsCount === 0) return null;
  const requiredSubjects = data.contextual_offers?.required_subjects
    || data.required_subjects || [];
  return (
    <section className="space-y-6 mb-8" data-testid="tab-admission-extras">
      <div>
        <TabKicker>02 · Admissions Landscape</TabKicker>
        <TabSectionTitle>
          {hasHist ? "The shape of last-cycle intake" : "Editorial admit band"}
        </TabSectionTitle>
      </div>
      <ChartCard
        title={hasHist ? "UCAS tariff distribution of admits" : "How your grades compare"}
        legend={
          <span>
            {hasHist
              ? "Modal band highlighted; drop your grades below to see where you'd land"
              : "Pick your grading system to see where you sit in the admit band"}
          </span>
        }
      >
        <GradesComparator uni={uni} onTariffChange={setUserTariff} />
        {hasHist && (
          <TariffHistogram
            dist={data.tariff_distribution}
            userTariff={userTariff}
          />
        )}
      </ChartCard>
      {requiredSubjects.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-2">
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#002147]/60 self-center mr-2">
            Required subjects
          </span>
          {requiredSubjects.map((s, i) => (
            <span key={i}
                  className="px-2 py-1 border border-[#002147]/20 font-mono text-xs text-[#002147] rounded-none">
              {typeof s === "string" ? s : s.subject}
            </span>
          ))}
        </div>
      )}
    </section>
  );
}

export default function TabAdmissionExtras({ uni }) {
  const data = useAdmissionSignals(uni.id);
  if (!data) return null;
  const hasRate = data.acceptance_rate != null;
  // Card visibility for the histogram lives inside the card itself
  // (it now covers both UCAS + editorial-band paths).
  return (
    <>
      {hasRate && <AcceptanceRateHero uni={uni} />}
      <TariffHistogramCard uni={uni} />
    </>
  );
}
